// React import removed
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import {
  BuildingOfficeIcon,
  UsersIcon,
  TrophyIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'

const AdminOverview: React.FC = () => {
  const navigate = useNavigate()

  const { data: systemStats, isLoading, error } = useQuery(
    'system-stats',
    () => api.get('/users/stats/system'),
    {
      select: (response) => response.data,
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error('❌ [ADMIN_OVERVIEW] System stats query failed:', error)
      },
      onSuccess: (data) => {
        console.log('✅ [ADMIN_OVERVIEW] System stats loaded successfully:', data)
      }
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    console.error('❌ [ADMIN_OVERVIEW] Rendering error state:', error)
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load dashboard</h2>
        <p className="text-gray-600 mb-4">Please try again later.</p>
        <details className="text-left bg-red-50 p-4 rounded-lg max-w-md mx-auto">
          <summary className="cursor-pointer text-red-700 font-medium">Error Details</summary>
          <pre className="mt-2 text-sm text-red-600 overflow-auto">
            {JSON.stringify(error, null, 2)}
          </pre>
        </details>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Reload Page
        </button>
      </div>
    )
  }

  // Debug logging
  console.log('🔍 [ADMIN_OVERVIEW] Current state:', {
    isLoading,
    error: error ? String(error) : null,
    systemStats,
    timestamp: new Date().toISOString()
  })

  const stats = [
    {
      title: 'Total Churches',
      value: systemStats?.totalChurches?.toString() || '0',
      change: systemStats?.pendingChurches ? `${systemStats.pendingChurches} pending approval` : 'No pending approvals',
      icon: BuildingOfficeIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Registered Participants',
      value: systemStats?.totalParticipants?.toString() || '0',
      change: `${systemStats?.totalCoordinators || 0} coordinators, ${systemStats?.totalJudges || 0} judges`,
      icon: UsersIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Active Competitions',
      value: systemStats?.totalCompetitions?.toString() || '0',
      change: systemStats?.totalCompetitions > 0 ? 'Competitions available' : 'No competitions yet',
      icon: TrophyIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: 'Total Tournaments',
      value: systemStats?.totalTournaments?.toString() || '0',
      change: systemStats?.totalTournaments > 0 ? 'Tournaments created' : 'No tournaments yet',
      icon: CalendarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ]

  const pendingActions: string[] = []
  if (systemStats?.pendingChurches > 0) {
    pendingActions.push(`${systemStats.pendingChurches} church registration${systemStats.pendingChurches === 1 ? '' : 's'} awaiting approval`)
  }
  if (systemStats?.pendingUsers > 0) {
    pendingActions.push(`${systemStats.pendingUsers} user registration${systemStats.pendingUsers === 1 ? '' : 's'} awaiting approval`)
  }
  if (pendingActions.length === 0) {
    pendingActions.push('No pending actions at this time')
  }

  const recentActivity = [
    {
      type: 'User Registration',
      message: `${systemStats?.totalParticipants || 0} participants registered`,
      time: 'Updated now'
    },
    {
      type: 'Church Approval',
      message: `${systemStats?.totalChurches || 0} churches approved`,
      time: 'Updated now'
    },
    {
      type: 'Role Assignment',
      message: `${systemStats?.totalCoordinators || 0} coordinators, ${systemStats?.totalJudges || 0} judges`,
      time: 'Updated now'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Overview of the Mahragan competition system
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.change}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h2>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.type}
                  </p>
                  <p className="text-sm text-gray-600">{activity.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/user-approval')}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <h3 className="font-medium text-gray-900">User Approvals</h3>
              <p className="text-sm text-gray-600">Review and approve pending coordinator and judge applications</p>
            </button>
            <button
              onClick={() => navigate('/admin/churches')}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <h3 className="font-medium text-gray-900">Manage Churches</h3>
              <p className="text-sm text-gray-600">View and manage church registrations</p>
            </button>
            <button
              onClick={() => navigate('/admin/users')}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <h3 className="font-medium text-gray-900">User Management</h3>
              <p className="text-sm text-gray-600">Create, edit, and manage user accounts</p>
            </button>
            <button
              onClick={() => navigate('/admin/competitions')}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <h3 className="font-medium text-gray-900">Competition Management</h3>
              <p className="text-sm text-gray-600">Create and manage competitions and tournaments</p>
            </button>
          </div>
        </div>
      </div>

      {/* Pending Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Pending Actions
        </h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Attention Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc pl-5 space-y-1">
                  {pendingActions.map((action, index) => (
                    <li key={index}>{action}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminOverview